'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CheckCircle2, Info, X, XCircle } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastInput {
  message: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
}

interface ToastItem extends Required<Pick<ToastInput, 'message'>> {
  id: string;
  description?: string;
  variant: ToastVariant;
  durationMs: number;
}

interface ToastContextValue {
  toast: (input: ToastInput | string) => void;
  success: (message: string, description?: string) => void;
  error: (message: string, description?: string) => void;
  info: (message: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const variantStyles: Record<
  ToastVariant,
  { border: string; bg: string; text: string; icon: React.ElementType }
> = {
  success: {
    border: 'border-success/30',
    bg: 'bg-success/10',
    text: 'text-success',
    icon: CheckCircle2,
  },
  error: {
    border: 'border-danger/30',
    bg: 'bg-danger/10',
    text: 'text-danger',
    icon: XCircle,
  },
  info: {
    border: 'border-primary/25',
    bg: 'bg-primary/10',
    text: 'text-primary',
    icon: Info,
  },
};

function ToastViewport({
  items,
  onDismiss,
}: {
  items: ToastItem[];
  onDismiss: (id: string) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2 px-4 sm:bottom-6 sm:right-6 sm:px-0"
      aria-live="polite"
      aria-relevant="additions"
    >
      {items.map((item) => {
        const cfg = variantStyles[item.variant];
        const Icon = cfg.icon;
        return (
          <div
            key={item.id}
            role="status"
            className={`pointer-events-auto animate-fade-in rounded-xl border px-4 py-3 shadow-lg backdrop-blur-sm ${cfg.border} ${cfg.bg}`}
          >
            <div className="flex items-start gap-3">
              <Icon size={18} className={`mt-0.5 shrink-0 ${cfg.text}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-semibold leading-snug ${cfg.text}`}>{item.message}</p>
                {item.description ? (
                  <p className="mt-1 break-all text-xs leading-relaxed text-foreground/80">
                    {item.description}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => onDismiss(item.id)}
                className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-black/5 hover:text-foreground"
                aria-label="Fechar notificação"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (input: ToastInput | string) => {
      const payload: ToastInput = typeof input === 'string' ? { message: input } : input;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const durationMs = payload.durationMs ?? 6000;
      const item: ToastItem = {
        id,
        message: payload.message,
        description: payload.description,
        variant: payload.variant ?? 'info',
        durationMs,
      };

      setItems((prev) => [...prev, item]);

      const timer = setTimeout(() => dismiss(id), durationMs);
      timersRef.current.set(id, timer);
    },
    [dismiss]
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      toast: push,
      success: (message, description) => push({ message, description, variant: 'success' }),
      error: (message, description) => push({ message, description, variant: 'error' }),
      info: (message, description) => push({ message, description, variant: 'info' }),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast deve ser usado dentro de ToastProvider');
  return ctx;
}
